import React, { useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    FormHelperText,
    Switch,
    FormControlLabel,
    TextField,
    Grid,
    FormControl,
    Select,
    MenuItem,
    RadioGroup,
    InputLabel,
    Radio,
    Card,
    CardContent,
    CardActions,
    Stack,
    Checkbox,
    useMediaQuery,
    Slide
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { addDays, addYears } from "date-fns";
import { enUS } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CustomTimePicker from "./CustomTimePicker";
import { ClientAddressInput } from "../../components/ClientAddressInput";
import MapComponent from "../../utils/CommonComonents/MapComponent";
import DateFrequencySelector from "../../components/DateFrequencySelector";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { postApiWithStatusCode } from "../../modules/AmplifyServices";
import { useAtom } from "jotai";
import {
    selectedUserAtom,
    selectedScheduleAtom,
    userPageSnackbarAtom,
    selectedUserServicesAtom,
    selectedUserSchedulesAtom,
    selectedAvailabilityScIdAtom,
    updateSelectedUserProviderLocationsAtom
} from "../../atoms/usersTable";
import {
    allActiveLocationsAtom,
    allActiveServicesAtom,
    mbxUserAtom,
    updateUserInAllUsersAtom,
    conformationDialogOpenAtom,
    conformationDialogContentAtom,
    confirmationFunctionAtom,
    conformationDialogTitleAtom,
    conformationDialogSubmitButtonAtom,
    conformationDialogIsSubmittingAtom,
    updateAllSchedulesAtom,
    companyAtom
} from "../../atoms/atoms";
import { extendGoogleMapsPolygon } from "../../modules/extendGoogleMapsPolygon";
import { formatToISODateOnly, prepareLocationPath } from "../../utils";
import { getCountryCode } from "../../utils";
import * as mutations from "../../graphql/mutations";
import { graphqlOperation, graphql } from "../../modules/AmplifyServices";
import {
    checkScheduleOverlap,
    checkTimeBlockOverlap
} from "../../utils/scheduleUtils";
import uuid from "uuid/v4";
import _ from "lodash";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@emotion/react";
import ArrowIcon from "../../images/ArrowIcon";
import { getTimezoneIdByGeoLoc } from "../../modules/TimeService";
import { createProviderTravelZoneAudit } from "../../utils/providerLocationUtils";
import InfoTooltip from "../../utils/UI/InfoTooltip";

const SlideTransition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const AvailabilityModal = ({ open, onClose, doneTodoHandler }) => {
    const [selectedSchedule] = useAtom(selectedScheduleAtom);
    const [, setSelectedAvailabilityScId] = useAtom(
        selectedAvailabilityScIdAtom
    );
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });

    const handleClose = () => {
        setSelectedAvailabilityScId(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            fullScreen={!isDesktop}
            TransitionComponent={!isDesktop ? SlideTransition : "Fade"}
        >
            <DialogTitle
                variant="h3"
                sx={{ gap: "1.125rem", paddingBottom: "18px" }}
            >
                <ArrowIcon
                    sx={{
                        display: isDesktop && "none",
                        fontSize: "18px",
                        ":hover": {
                            cursor: "pointer"
                        }
                    }}
                    onClick={handleClose}
                />
                {selectedSchedule ? "Edit availability " : "Add availability"}
            </DialogTitle>
            <AvailabilityForm
                onClose={handleClose}
                doneTodoHandler={doneTodoHandler}
            />
        </Dialog>
    );
};

export default AvailabilityModal;

const AvailabilityForm = ({ onClose, doneTodoHandler }) => {
    const theme = useTheme();
    const autocompleteService = { current: null };
    const [mbxUser] = useAtom(mbxUserAtom);
    const [company] = useAtom(companyAtom);
    const [selectedUser] = useAtom(selectedUserAtom);
    const [selectedUserSchedules] = useAtom(selectedUserSchedulesAtom);
    const [selectedSchedule] = useAtom(selectedScheduleAtom);
    const [, updateSelectedUserProviderLocations] = useAtom(
        updateSelectedUserProviderLocationsAtom
    );
    const [, setSelectedAvailabilityScId] = useAtom(
        selectedAvailabilityScIdAtom
    );
    const [allActiveLocations] = useAtom(allActiveLocationsAtom);
    const [allActiveServices] = useAtom(allActiveServicesAtom);
    const [selectedUserServices] = useAtom(selectedUserServicesAtom);
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);
    const [, setConfirmationDialogContent] = useAtom(
        conformationDialogContentAtom
    );
    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [, setConfirmationDialogOpen] = useAtom(conformationDialogOpenAtom);
    const [, setConformationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [, setConformationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const [, setConformationDialogIsSubmitting] = useAtom(
        conformationDialogIsSubmittingAtom
    );
    const [, updateAllSchedules] = useAtom(updateAllSchedulesAtom);
    const [, updateUserInAllUsers] = useAtom(updateUserInAllUsersAtom);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const endTimeRefs = useRef([]);
    const availabilityFormRef = useRef(null);
    const startDateRef = useRef(null);

    const timeRangeConstructor = () => {
        const startTime = new Date().setHours(9, 0, 0, 0);
        const endTime = new Date().setHours(17, 0, 0, 0);
        let block = {
            startTime,
            endTime
        };
        if (selectedSchedule) {
            block = {
                ...block,
                id: `SB|${selectedSchedule.scheduleinfo}|${uuid()}`
            };
        }
        return block;
    };

    const handleAddTimeBlock = () => {
        const lastEndTime = timeBlockFields.length
            ? timeBlockFields[timeBlockFields.length - 1].endTime
            : new Date().setHours(17, 0, 0, 0);

        const newStartTime = new Date(lastEndTime).setHours(
            new Date(lastEndTime).getHours() + 1
        ); // One hour later than last end time

        const newTimeBlock = {
            startTime: newStartTime,
            endTime: new Date(newStartTime).setHours(
                new Date(newStartTime).getHours() + 1
            ) // End time defualts to 8 hours after the start time
        };

        if (selectedSchedule) {
            newTimeBlock.id = `SB|${selectedSchedule.scheduleinfo}|${uuid()}`;
        }

        append(newTimeBlock);
    };

    const {
        SB = [],
        SL = [],
        SR = [],
        id,
        active = true,
        scheduleinfo = "",
        providerId,
        travelZone = null
    } = selectedSchedule || {};
    const services = selectedSchedule
        ? JSON.parse(selectedSchedule.services)
        : _.map(selectedUserServices, "id");

    const visibleServices = selectedUser
        ? selectedUserServices
        : allActiveServices;
    const locations = selectedSchedule
        ? JSON.parse(selectedSchedule.locations)
              .filter((location) => location.startsWith("CL-"))
              .map((item) => item.substring(3)) //the selectedSchedule.locations will contain Provider travel zone locations (begin with PL-), so we must filter out only company locations for the Locations drop down
        : _.map(allActiveLocations, "id");
    const timeBlocks = SB.length
        ? SB.map((sb) => {
              return {
                  id: sb.scheduleinfo,
                  startTime: new Date().setHours(
                      ...sb.startTime.split(":").map(Number)
                  ),
                  endTime: new Date().setHours(
                      ...sb.endTime.split(":").map(Number)
                  )
              };
          })
        : [timeRangeConstructor()];

    const form = useForm({
        defaultValues: {
            startDate: SB.length
                ? new Date(
                      ...SB[0].startDate
                          .split("-")
                          .map((part, index) => (index === 1 ? part - 1 : part))
                  )
                : new Date(),
            endDate: SB.length
                ? new Date(
                      ...SB[0].endDate
                          .split("-")
                          .map((part, index) => (index === 1 ? part - 1 : part))
                  )
                : new Date(
                      new Date().setFullYear(new Date().getFullYear() + 10)
                  ),
            numberSelector: {
                selectedDays: SB.length
                    ? SB[0].weekDays.split("").map(Number)
                    : [1, 2, 3, 4, 5],
                selectedNumber: SB.length ? SB[0].weeksToRepeat : 1
            },
            address: travelZone ? travelZone.addressoneline : "",
            addressAttributes: {
                street: travelZone ? travelZone.addressoneline : "",
                city: travelZone ? travelZone.city : "",
                state: travelZone ? travelZone.state : "",
                country: travelZone ? travelZone.country : "",
                postalcode: travelZone ? travelZone.postalcode : "",
                longitude: travelZone ? travelZone.longitude : null,
                latitude: travelZone ? travelZone.latitude : null
            },
            services: services || [],
            includePhysicalVirtualInTravelZone:
                travelZone &&
                !SL.some((record) => record.scheduleinfo.includes(`CL-`))
                    ? false
                    : true,
            locations: locations || [],
            polygons: travelZone ? JSON.parse(travelZone.travelregions) : [],
            travelType: travelZone?.maxtraveltype
                ? travelZone.maxtraveltype
                : travelZone
                  ? "MAX_DISTANCE"
                  : selectedSchedule
                    ? "COMPANY_LOCATIONS"
                    : "DRAW_TRAVEL_REGION",
            travelDistance: travelZone ? travelZone.traveldistance : 25,
            travelDistanceUnit: travelZone
                ? travelZone.traveldistanceunit === "km"
                    ? "kilometer"
                    : "mile"
                : mbxUser?.company?.country === "United States" ||
                    mbxUser?.company?.country === "United Kingdom"
                  ? "mile"
                  : "kilometer",
            timeBlocks
        }
    });
    const {
        handleSubmit,
        setValue,
        getValues,
        setError,
        clearErrors,
        control,
        watch,
        formState
    } = form;
    const { errors, isDirty, dirtyFields } = formState;
    const travelType = watch("travelType");
    const travelDistance = watch("travelDistance");
    const travelDistanceUnit = watch("travelDistanceUnit");
    const addressAttributes = watch("addressAttributes");
    const address = watch("address");
    const polygons = watch("polygons");
    const includePhysicalVirtualInTravelZone = watch(
        "includePhysicalVirtualInTravelZone"
    );
    const {
        fields: timeBlockFields,
        append,
        remove
    } = useFieldArray({
        control,
        name: "timeBlocks"
    });

    /* START MAP FUNCTIONS */
    const drawTypeMap = {
        MAX_DISTANCE: 1,
        DRAW_TRAVEL_REGION: 2,
        COMPANY_LOCATIONS: 3
    };

    const checkLocationsInPolygons = (locations, polygons) => {
        //Ensure the Google Maps Polygon prototype is extended
        extendGoogleMapsPolygon();

        if (polygons.length === 0) {
            return locations;
        }

        const result = [];

        locations.forEach((location) => {
            const { latitude: lat, longitude: lng } = location;
            const point = new window.google.maps.LatLng(lat, lng);

            let isInsideAnyPolygon = false;

            for (const polygonPaths of polygons) {
                const polygon = new window.google.maps.Polygon({
                    paths: polygonPaths
                });

                if (polygon.containsLatLng(point)) {
                    isInsideAnyPolygon = true;
                    break;
                }
            }

            if (isInsideAnyPolygon) {
                result.push(location);
            }
        });

        return result;
    };

    const checkLocationsInCircle = (locations, centerPoint, radius) => {
        if (!centerPoint) {
            return locations;
        }

        const result = [];

        locations.forEach((location) => {
            const { latitude: lat, longitude: lng } = location;

            const isInsideCircle = () => {
                var ky = 40000 / 360;
                var kx = Math.cos((Math.PI * centerPoint.lat) / 180.0) * ky;
                var dx = Math.abs(centerPoint.lng - lng) * kx;
                var dy = Math.abs(centerPoint.lat - lat) * ky;
                return Math.sqrt(dx * dx + dy * dy) <= radius;
            };
            if (isInsideCircle()) {
                result.push(location);
            }
        });
        return result;
    };
    /* END MAP FUNCTIONS */

    const isTravelZoneDirty = () => {
        const travelZoneFields = [
            "address",
            "polygons",
            "travelDistance",
            "travelDistanceUnit",
            "travelType"
        ];
        return _.some(
            travelZoneFields,
            (field) => _.get(dirtyFields, field) === true
        );
    };

    const getValidLocations = () => {
        switch (travelType) {
            case "DRAW_TRAVEL_REGION":
                return checkLocationsInPolygons(allActiveLocations, polygons);
            case "MAX_DISTANCE":
                return address
                    ? checkLocationsInCircle(
                          allActiveLocations,
                          {
                              lat: addressAttributes.latitude,
                              lng: addressAttributes.longitude
                          },
                          travelDistance *
                              (travelDistanceUnit === "kilometer" ? 1 : 1.60934)
                      )
                    : allActiveLocations;
            case "COMPANY_LOCATIONS":
                return allActiveLocations;
            default:
                return allActiveLocations;
        }
    };

    const saveProviderTravelZone = async () => {
        const {
            street = null,
            city = null,
            state = null,
            country = null,
            postalcode = null,
            longitude = null,
            latitude = null
        } = addressAttributes || {};
        try {
            let timezone;
            if (longitude && latitude) {
                timezone = await getTimezoneIdByGeoLoc({
                    lat: latitude,
                    lng: longitude
                });
            } else {
                timezone = selectedUser.provider.timezone;
            }

            let input = {
                name: `Travel zone`,
                addressoneline: address,
                street,
                city,
                state,
                country,
                postalcode,
                longitude,
                latitude,
                companyId: company.id,
                traveldistance: travelDistance,
                traveldistanceunit:
                    travelDistanceUnit === "kilometer" ? "km" : "mi",
                travelregions: JSON.stringify(polygons),
                providerId: selectedUser.provider.id,
                providerLocationProviderId: selectedUser.provider.id,
                providerLocationCompanyId: company.id,
                locationpath: prepareLocationPath(
                    getCountryCode(country),
                    state,
                    city,
                    postalcode
                ),
                timezone,
                maxtraveltype: travelType
            };
            let response = travelZone;
            if (!selectedSchedule?.travelZone) {
                input.createdAt = new Date();
                let providerLocation = await graphql(
                    graphqlOperation(mutations.createProviderLocation, {
                        input
                    })
                );
                if (providerLocation?.data?.createProviderLocation) {
                    response = providerLocation.data.createProviderLocation;
                    await createProviderTravelZoneAudit({
                        actingUser: mbxUser,
                        provider: {
                            firstname: selectedUser.firstname,
                            lastname: selectedUser.lastname,
                            id: selectedUser.provider.id
                        },
                        userAction: "CREATE",
                        resourceId: `${response.id}`,
                        schedChange: response,
                        schedBlock: "CREATE_TRAVEL_ZONE"
                    });
                }
            } else if (isTravelZoneDirty()) {
                input = {
                    ...input,
                    id: selectedSchedule.travelZone.id
                };
                let providerLocation = await graphql(
                    graphqlOperation(mutations.updateProviderLocation, {
                        input
                    })
                );
                if (providerLocation?.data?.updateProviderLocation) {
                    response = providerLocation.data.updateProviderLocation;
                    await createProviderTravelZoneAudit({
                        actingUser: mbxUser,
                        provider: {
                            firstname: selectedUser.firstname,
                            lastname: selectedUser.lastname,
                            id: selectedUser.provider.id
                        },
                        userAction: "UPDATE",
                        resourceId: `${response.id}`,
                        schedChange: response,
                        schedBlock: "UPDATE_TRAVEL_ZONE",
                        newInfo: dirtyFields
                    });
                }
            }
            return response;
        } catch (error) {
            console.log("Unable to update/add provider travel zone", error);
        }
    };

    const CustomTimePickerValidation = (data) => {
        // Check if timeBlocks exists and is an array
        if (!data.timeBlocks || !Array.isArray(data.timeBlocks)) {
            console.error("Invalid timeBlocks data");
            return;
        }

        // Validation for startTime and endTime
        let hasError = false;

        for (const [index, timeBlock] of data.timeBlocks.entries()) {
            const { startTime, endTime } = timeBlock;

            if (endTime <= startTime) {
                setError(`timeBlocks.${index}.endTime`, {
                    type: "required",
                    message: "End time must be after start time"
                });
                hasError = true;

                // Scroll to the first error using ref
                if (endTimeRefs.current[index]) {
                    endTimeRefs.current[index].scrollIntoView({
                        behavior: "smooth",
                        block: "center"
                    });
                }
                break;
            }
        }

        return hasError;
    };

    const onSubmit = async (data) => {
        const customTimePickerError = CustomTimePickerValidation(data);

        if (customTimePickerError) {
            return;
        }

        const options = { hour: "2-digit", minute: "2-digit", hour12: false };
        const adjustedTimeBlocks = data.timeBlocks.map((timeBlock) => {
            return {
                ...timeBlock,
                startTime: new Date(timeBlock.startTime).toLocaleTimeString(
                    "en-US",
                    options
                ),
                endTime: new Date(timeBlock.endTime).toLocaleTimeString(
                    "en-US",
                    options
                )
            };
        });
        //Ensure the locations array only includes valid locations
        const validLocations =
            travelType !== "COMPANY_LOCATIONS" &&
            !includePhysicalVirtualInTravelZone
                ? []
                : getValidLocations();
        const sanitizedLocations = data.locations.filter((loc) =>
            validLocations.some((validLoc) => validLoc.id === loc)
        );

        //Validate polygons when in draw zone mode only
        if (travelType === "DRAW_TRAVEL_REGION" && polygons.length === 0) {
            setError("polygons", {
                type: "manual",
                message: "Please draw at least one zone"
            });
            return;
        }

        let avdata = {
            active: true,
            timezone: selectedUser.provider.timezone,
            companyId: mbxUser.companyId,
            providerId: selectedUser.provider.id,
            weeksToRepeat: data.numberSelector.selectedNumber,
            weekDays: data.numberSelector.selectedDays,
            services: data.services,
            locations: sanitizedLocations.map((loc) => {
                return `CL-${loc}`;
            }),
            timeBlocks: adjustedTimeBlocks,
            startDate: formatToISODateOnly(data.startDate),
            endDate: formatToISODateOnly(data.endDate)
        };

        if (checkTimeBlockOverlap(avdata.timeBlocks)) {
            setUserPageSnackbar({
                open: true,
                message: `The selected times overlap. Please choose non-overlapping time ranges`,
                severity: "error"
            });
            return;
        }
        if (selectedUserSchedules?.length) {
            const overlapResult = handleCheckScheduleOverlap(avdata);
            if (overlapResult) {
                setUserPageSnackbar({
                    open: true,
                    message: `Schedule overlaps with existing schedule`,
                    severity: "error"
                });
                return;
            }
        }

        setIsSubmitting(true);
        try {
            if (travelType !== "COMPANY_LOCATIONS") {
                try {
                    //attempt to save the provider travel zone
                    const response = await saveProviderTravelZone();
                    avdata = {
                        ...avdata,
                        locations: [...avdata.locations, `PL-${response.id}`]
                    };

                    updateSelectedUserProviderLocations(response);
                } catch (error) {
                    console.error(
                        "Error occurred while saving provider travel zone:",
                        error
                    );
                }
            }
            if (!selectedSchedule) {
                //create route
                const { statusCode: status, body: newAvailabilityRecord } =
                    await postApiWithStatusCode(
                        "userManagement",
                        "/user/availability",
                        {
                            body: avdata
                        }
                    );
                if (newAvailabilityRecord && status >= 200 && status < 300) {
                    onSuccess(
                        newAvailabilityRecord,
                        "Availability has been successfully created"
                    );
                } else {
                    onFailure(
                        status,
                        "Unexpected error: The availability was not created"
                    );
                    throw new Error(
                        `Failed to create new availability. Error: ${newAvailabilityRecord.message}`
                    );
                }
            } else {
                //edit route

                const { statusCode: status, body: updatedAvailabilityRecord } =
                    await postApiWithStatusCode(
                        "userManagement",
                        `/user/availability`,
                        {
                            body: {
                                ...avdata,
                                availabilityId: selectedSchedule?.scheduleinfo,
                                action: "EDIT"
                            }
                        }
                    );
                if (
                    updatedAvailabilityRecord &&
                    status >= 200 &&
                    status < 300
                ) {
                    onSuccess(
                        updatedAvailabilityRecord,
                        "Availability has been successfully updated"
                    );
                } else {
                    onFailure(
                        status,
                        "Unexpected error: The availability was not updated"
                    );
                    throw new Error(
                        `Failed to update availability. Error: ${updatedAvailabilityRecord.message}`
                    );
                }
            }
        } catch (error) {
            console.log("Error submitting form data:", error);
            setIsSubmitting(false);
        }
    };

    function handleCheckScheduleOverlap(data) {
        const schedulesToCheck = selectedSchedule
            ? selectedUserSchedules.filter(
                  (schedule) =>
                      schedule.scheduleinfo !== selectedSchedule.scheduleinfo
              )
            : selectedUserSchedules;

        return data.timeBlocks.some((timeBlock) =>
            checkScheduleOverlap(
                data.startDate,
                data.endDate,
                timeBlock.startTime,
                timeBlock.endTime,
                data.weeksToRepeat,
                data.weekDays,
                data.services,
                schedulesToCheck
            )
        );
    }

    const onSuccess = async (availabilityData, successMessage) => {
        let oldRecords = [];
        if (selectedSchedule) {
            oldRecords = [
                {
                    id: selectedSchedule.id,
                    scheduleinfo: selectedSchedule.scheduleinfo
                }
            ];
        }
        updateAllSchedules(oldRecords, availabilityData.records);
        if (doneTodoHandler) doneTodoHandler("availability");
        setUserPageSnackbar({
            open: true,
            message: successMessage,
            severity: "success"
        });
        setIsSubmitting(false);
        onClose();
    };

    const onFailure = (status, errorMessage) => {
        setUserPageSnackbar({
            open: true,
            message: `${errorMessage} ${status ? `(${status})` : ""}`,
            severity: "error"
        });
    };

    const handleDelete = async () => {
        setConformationDialogIsSubmitting(true);
        const { statusCode } = await postApiWithStatusCode(
            "userManagement",
            `/user/availability`,
            {
                body: {
                    companyId: company.id,
                    providerId,
                    availabilityId: selectedSchedule.scheduleinfo,
                    action: "DELETE"
                }
            }
        );

        if (statusCode >= 200 && statusCode < 300) {
            let oldRecords = [
                {
                    id: selectedSchedule.id,
                    scheduleinfo: selectedSchedule.scheduleinfo
                }
            ];
            updateAllSchedules(oldRecords, []);
            setUserPageSnackbar({
                open: true,
                message: `Availability has been successfully deleted`,
                severity: "success"
            });
        } else {
            console.log(statusCode);
            setUserPageSnackbar({
                open: true,
                message: `Unexpected Error: Unable to delete availability`,
                severity: "error"
            });
        }
        setConfirmationDialogOpen(false);
        setConformationDialogIsSubmitting(false);
        handleClose();
    };

    const handleClose = () => {
        onClose();
    };

    const handleDuplicate = () => {
        const formValues = getValues();

        const originalEndDate = new Date(formValues.endDate);

        // New start date = original end date + 1 day
        const newStartDate = addDays(originalEndDate, 1);

        // New end date = new start date + 10 years
        const newEndDate = addYears(newStartDate, 10);

        // Prepare duplicated data (keeping everything except dates)
        const duplicatedData = {
            ...formValues,
            startDate: newStartDate,
            endDate: newEndDate
        };

        // Reset the form with duplicated data
        Object.entries(duplicatedData).forEach(([key, value]) => {
            setValue(key, value, { shouldDirty: true });
        });

        setSelectedAvailabilityScId(null);

        // Scroll to the top of the dialog content amd focus on start date
        if (availabilityFormRef?.current) {
            availabilityFormRef.current.scrollTo({
                top: 0,
                behavior: "smooth"
            });
            startDateRef.current.focus();
        }

        setUserPageSnackbar({
            open: true,
            message: "Availability duplicated – please select new date range",
            severity: "success"
        });
    };

    return (
        <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            style={{
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                height: "100%"
            }}
        >
            <DialogContent ref={availabilityFormRef}>
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={{
                        ...enUS,
                        options: { ...enUS.options, weekStartsOn: 1 }
                    }}
                >
                    <Grid container spacing={2.25} sx={{ marginTop: "0px" }}>
                        <Grid item xs={6} sm={6}>
                            <Controller
                                name="startDate"
                                control={control}
                                render={({
                                    field: {
                                        onChange: onChangeFormHook,
                                        value: startDate
                                    }
                                }) => (
                                    <DatePicker
                                        autoFocus
                                        id="start-date"
                                        name="startDate"
                                        size="small"
                                        label="Start date"
                                        value={startDate}
                                        fullWidth
                                        inputFormat="MMM d, yyyy"
                                        onChange={(newValue) => {
                                            onChangeFormHook(newValue);
                                            newValue > getValues("endDate") &&
                                                setValue("endDate", newValue);
                                        }}
                                        open={openStartDate}
                                        onOpen={() => setOpenStartDate(true)}
                                        onClose={() => {
                                            setOpenStartDate(false);
                                            setAnchorEl(null);
                                        }}
                                        PopperProps={{
                                            anchorEl: anchorEl,
                                            disablePortal: false
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                inputRef={startDateRef}
                                                fullWidth
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true
                                                }}
                                                onClick={(e) => {
                                                    setAnchorEl(
                                                        e.currentTarget
                                                    );
                                                    setOpenStartDate(true);
                                                }}
                                                sx={{
                                                    "& .MuiInputAdornment-root":
                                                        {
                                                            display: "none"
                                                        },
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "54px"
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <Controller
                                name="endDate"
                                control={control}
                                render={({
                                    field: {
                                        onChange: onChangeFormHook,
                                        value: endDate
                                    }
                                }) => (
                                    <DatePicker
                                        id="end-date"
                                        name="endDate"
                                        size="small"
                                        label="End date"
                                        value={endDate}
                                        minDate={getValues("startDate")}
                                        fullWidth
                                        inputFormat="MMM d, yyyy"
                                        onChange={onChangeFormHook}
                                        open={openEndDate}
                                        onOpen={() => setOpenEndDate(true)}
                                        onClose={() => setOpenEndDate(false)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true
                                                }}
                                                onClick={(e) =>
                                                    setOpenEndDate(true)
                                                }
                                                sx={{
                                                    "& .MuiInputAdornment-root":
                                                        {
                                                            display: "none"
                                                        },
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "50px"
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid
                            container
                            item
                            xs={12}
                            sm={12}
                            sx={{
                                alignItems: "center",
                                justifyContent: "space-between"
                            }}
                        >
                            <Controller
                                name="numberSelector"
                                control={control}
                                rules={{
                                    required:
                                        "Please select at least one day to repeat on"
                                }}
                                render={({
                                    field: {
                                        onChange: onChangeFormHook,
                                        value: { selectedDays, selectedNumber }
                                    }
                                }) => (
                                    <DateFrequencySelector
                                        handleNumberSelectorChange={({
                                            target: { value }
                                        }) => {
                                            onChangeFormHook({
                                                selectedDays,
                                                selectedNumber: value
                                            });
                                        }}
                                        handleDayButtonClick={(day) => {
                                            if (
                                                selectedDays.length === 1 &&
                                                selectedDays[0] === day
                                            ) {
                                                return;
                                            }
                                            const updatedDays =
                                                selectedDays.includes(day)
                                                    ? selectedDays.filter(
                                                          (d) => d !== day
                                                      )
                                                    : [...selectedDays, day];
                                            updatedDays.sort((a, b) => a - b);
                                            onChangeFormHook({
                                                selectedDays: updatedDays,
                                                selectedNumber
                                            });
                                        }}
                                        selectedDays={selectedDays}
                                        selectedNumber={selectedNumber}
                                    />
                                )}
                            />
                            {!!errors.numberSelector && (
                                <FormHelperText error>
                                    {errors?.numberSelector.message}
                                </FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} sm={12}>
                            <Card
                                variant="outlined"
                                sx={{ width: "100%", borderColor: "black" }}
                            >
                                <CardContent>
                                    {timeBlockFields.map((item, index) => (
                                        <Stack key={item.scheduleinfo}>
                                            <Grid item xs={12} sm={6}>
                                                <Controller
                                                    name={`timeBlocks.${index}.startTime`}
                                                    control={control}
                                                    render={({ field }) => {
                                                        const {
                                                            onChange:
                                                                onChangeFormHook,
                                                            value: startTime
                                                        } = field;
                                                        return (
                                                            <>
                                                                <CustomTimePicker
                                                                    id="start-time"
                                                                    name={`timeBlocks.${index}.startTime`}
                                                                    type="startTime"
                                                                    onChange={(
                                                                        timestamp
                                                                    ) => {
                                                                        clearErrors(
                                                                            `timeBlocks.${index}.endTime`
                                                                        );
                                                                        onChangeFormHook(
                                                                            timestamp
                                                                        );
                                                                    }}
                                                                    value={
                                                                        startTime
                                                                    }
                                                                />
                                                                {formState
                                                                    .errors
                                                                    ?.timeBlocks?.[
                                                                    index
                                                                ]?.endTime && (
                                                                    <Grid>
                                                                        <Typography variant="errorText">
                                                                            {
                                                                                formState
                                                                                    .errors
                                                                                    .timeBlocks[
                                                                                    index
                                                                                ]
                                                                                    .endTime
                                                                                    .message
                                                                            }
                                                                        </Typography>
                                                                    </Grid>
                                                                )}
                                                            </>
                                                        );
                                                    }}
                                                />
                                            </Grid>

                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                display={"flex"}
                                                alignItems={"center"}
                                                gap={"10px"}
                                            >
                                                <Controller
                                                    name={`timeBlocks.${index}.endTime`}
                                                    control={control}
                                                    render={({ field }) => {
                                                        const {
                                                            onChange:
                                                                onChangeFormHook,
                                                            value: endTime
                                                        } = field;
                                                        return (
                                                            <Grid
                                                                container
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    flexDirection:
                                                                        "column"
                                                                }}
                                                            >
                                                                <CustomTimePicker
                                                                    id="end-time"
                                                                    name={`timeBlocks.${index}.endTime`}
                                                                    ref={(
                                                                        el
                                                                    ) => {
                                                                        endTimeRefs.current[
                                                                            index
                                                                        ] = el;
                                                                    }}
                                                                    type="endTime"
                                                                    error={
                                                                        !!formState
                                                                            .errors
                                                                            ?.timeBlocks?.[
                                                                            index
                                                                        ]
                                                                            ?.endTime
                                                                    }
                                                                    onChange={(
                                                                        timestamp
                                                                    ) => {
                                                                        clearErrors(
                                                                            `timeBlocks.${index}.endTime`
                                                                        );
                                                                        onChangeFormHook(
                                                                            timestamp
                                                                        );
                                                                    }}
                                                                    value={
                                                                        endTime
                                                                    }
                                                                />
                                                                {formState
                                                                    .errors
                                                                    ?.timeBlocks?.[
                                                                    index
                                                                ]?.endTime && (
                                                                    <Grid
                                                                        sx={{
                                                                            minHeight:
                                                                                "21px"
                                                                        }}
                                                                    >
                                                                        <Typography>
                                                                            {" "}
                                                                        </Typography>
                                                                    </Grid>
                                                                )}
                                                            </Grid>
                                                        );
                                                    }}
                                                />
                                                {index > 0 && (
                                                    <DeleteIcon
                                                        type="button"
                                                        variant="deleteIcon"
                                                        onClick={() => {
                                                            timeBlockFields.length >
                                                                1 &&
                                                                remove(index);
                                                        }}
                                                    />
                                                )}
                                            </Grid>
                                        </Stack>
                                    ))}
                                </CardContent>
                                <CardActions>
                                    <Button
                                        variant="cancelButton"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddTimeBlock}
                                    >
                                        <b> Add times</b>
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="travel-type-label" size="small">
                                    Coverage area
                                </InputLabel>
                                <Controller
                                    name="travelType"
                                    control={control}
                                    render={({
                                        field: {
                                            onChange: onChangeFormHook,
                                            value: travelType
                                        }
                                    }) => (
                                        <Select
                                            labelId="coverage-area-label"
                                            id="travel-type"
                                            name="travelType"
                                            label="Coverage area"
                                            value={travelType}
                                            sx={{
                                                "& fieldset": {
                                                    "& legend": {
                                                        width: "75px"
                                                    }
                                                }
                                            }}
                                            onChange={(event) => {
                                                onChangeFormHook(
                                                    event.target.value
                                                );
                                                setValue(
                                                    "locations",
                                                    _.map(
                                                        allActiveLocations,
                                                        "id"
                                                    )
                                                );
                                                clearErrors("polygons");
                                            }}
                                        >
                                            <MenuItem
                                                value={"DRAW_TRAVEL_REGION"}
                                            >
                                                Drawn travel zone
                                            </MenuItem>
                                            <MenuItem value={"MAX_DISTANCE"}>
                                                Circular travel zone
                                            </MenuItem>
                                            {allActiveLocations?.length > 0 && (
                                                <MenuItem
                                                    value={"COMPANY_LOCATIONS"}
                                                >
                                                    Company locations
                                                </MenuItem>
                                            )}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        {getValues("travelType") !== "COMPANY_LOCATIONS" && (
                            <>
                                <Grid item xs={6} sm={6} md={3}>
                                    <Controller
                                        name="travelDistance"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value: travelDistance
                                            }
                                        }) => (
                                            <TextField
                                                id="travel-distance"
                                                name="travelDistance"
                                                size="small"
                                                label="Radius"
                                                type="number"
                                                value={travelDistance}
                                                InputLabelProps={{
                                                    shrink: true
                                                }}
                                                disabled={
                                                    travelType !==
                                                    "MAX_DISTANCE"
                                                }
                                                sx={{
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "40px"
                                                        }
                                                    }
                                                }}
                                                onChange={(event) => {
                                                    const newValue =
                                                        event.target.value;

                                                    // Allow only positive integers, no decimals or negative numbers
                                                    if (
                                                        newValue === "" ||
                                                        /^[1-9][0-9]*$/.test(
                                                            newValue
                                                        )
                                                    ) {
                                                        onChangeFormHook(
                                                            newValue
                                                        );
                                                    }
                                                }}
                                                onKeyDown={(event) => {
                                                    // Prevent typing of any non-numeric characters except Backspace and Tab
                                                    if (
                                                        !/[0-9]/.test(
                                                            event.key
                                                        ) &&
                                                        event.key !==
                                                            "Backspace" &&
                                                        event.key !== "Tab"
                                                    ) {
                                                        event.preventDefault();
                                                    }
                                                }}
                                                onBlur={() => {
                                                    // If the field is empty on blur, set it to a default value of 25
                                                    if (travelDistance === "") {
                                                        onChangeFormHook("25");
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={6} md={3}>
                                    <FormControl component="fieldset">
                                        <Controller
                                            name="travelDistanceUnit"
                                            control={control}
                                            render={({
                                                field: {
                                                    onChange: onChangeFormHook,
                                                    value: travelDistanceUnit
                                                }
                                            }) => (
                                                <RadioGroup
                                                    aria-label="travel-distance-unit"
                                                    name="travelDistanceUnit"
                                                    sx={{
                                                        flexDirection: "row"
                                                    }}
                                                    value={travelDistanceUnit}
                                                    onChange={(event) =>
                                                        onChangeFormHook(
                                                            event.target.value
                                                        )
                                                    }
                                                >
                                                    <FormControlLabel
                                                        value="kilometer"
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                disabled={
                                                                    getValues(
                                                                        "travelType"
                                                                    ) !==
                                                                    "MAX_DISTANCE"
                                                                }
                                                            />
                                                        }
                                                        label="km"
                                                    />
                                                    <FormControlLabel
                                                        value="mile"
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                disabled={
                                                                    getValues(
                                                                        "travelType"
                                                                    ) !==
                                                                    "MAX_DISTANCE"
                                                                }
                                                            />
                                                        }
                                                        label="mi"
                                                    />
                                                </RadioGroup>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                        {getValidLocations().length > 0 && (
                            <Grid
                                item
                                xs={12}
                                sm={12}
                                sx={{
                                    display:
                                        travelType === "COMPANY_LOCATIONS"
                                            ? "none"
                                            : ""
                                }}
                            >
                                <Controller
                                    name="includePhysicalVirtualInTravelZone"
                                    control={control}
                                    render={({
                                        field: {
                                            onChange: onChangeFormHook,
                                            value
                                        }
                                    }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    id="include-physical-virtual"
                                                    name="includePhysicalVirtualInTravelZone"
                                                    checked={value}
                                                    value={value}
                                                    onChange={onChangeFormHook}
                                                />
                                            }
                                            label="Include available company locations inside travel zone"
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                        {(travelType === "COMPANY_LOCATIONS" ||
                            includePhysicalVirtualInTravelZone) &&
                            getValidLocations().length > 0 && (
                                <Grid item xs={12} sm={12}>
                                    <FormControl fullWidth sx={{ gap: 0.5 }}>
                                        <InputLabel
                                            id="locations-label"
                                            size="small"
                                        >
                                            Locations
                                        </InputLabel>
                                        <Controller
                                            name="locations"
                                            control={control}
                                            rules={{
                                                required:
                                                    "Please select at least one location",
                                                validate: {
                                                    validateLocations: (
                                                        fieldValue
                                                    ) =>
                                                        travelType !==
                                                        "COMPANY_LOCATIONS"
                                                            ? true
                                                            : fieldValue.length >
                                                                0
                                                              ? true
                                                              : getValidLocations()
                                                                      .length ===
                                                                  0
                                                                ? true
                                                                : false
                                                }
                                            }}
                                            render={({
                                                field: {
                                                    onChange: onChangeFormHook,
                                                    value: locations
                                                }
                                            }) => (
                                                <Select
                                                    labelId="locations-label"
                                                    id="locations"
                                                    name="locations"
                                                    label="Locations"
                                                    multiple
                                                    value={locations}
                                                    sx={{
                                                        "& fieldset": {
                                                            "& legend": {
                                                                width: "55px"
                                                            }
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        onChangeFormHook(
                                                            e.target.value
                                                        );
                                                    }}
                                                    renderValue={() =>
                                                        locations.length ===
                                                        allActiveLocations.length
                                                            ? "All locations"
                                                            : allActiveLocations
                                                                  .filter(
                                                                      (
                                                                          location
                                                                      ) =>
                                                                          locations.includes(
                                                                              location.id
                                                                          )
                                                                  )
                                                                  .map(
                                                                      (
                                                                          location
                                                                      ) =>
                                                                          location.locationname
                                                                  )
                                                                  .join(", ")
                                                    }
                                                >
                                                    {getValidLocations().map(
                                                        (location) => (
                                                            <MenuItem
                                                                value={
                                                                    location.id
                                                                }
                                                            >
                                                                <Checkbox
                                                                    checked={locations.includes(
                                                                        location.id
                                                                    )}
                                                                />
                                                                {
                                                                    location.locationname
                                                                }
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            )}
                                        />
                                        {!!errors.locations && (
                                            <FormHelperText error>
                                                {errors?.locations.message}
                                            </FormHelperText>
                                        )}
                                        <InfoTooltip
                                            title="You can be available at any of
                                                the above locations during your
                                                working hours – they will be
                                                treated as any other residential
                                                address for travel time
                                                calculations between bookings"
                                        />
                                    </FormControl>
                                </Grid>
                            )}
                        <Grid item xs={12} sm={12}>
                            <FormControl fullWidth>
                                <InputLabel id="services-label" size="small">
                                    Services
                                </InputLabel>
                                <Controller
                                    name="services"
                                    control={control}
                                    rules={{
                                        required:
                                            "Please select at least one service"
                                    }}
                                    render={({
                                        field: {
                                            onChange: onChangeFormHook,
                                            value: services
                                        }
                                    }) => {
                                        const validServices = services.filter(
                                            (serviceId) =>
                                                allActiveServices.some(
                                                    (service) =>
                                                        service.id === serviceId
                                                )
                                        );

                                        if (
                                            validServices.length !==
                                            services.length
                                        ) {
                                            onChangeFormHook(validServices);
                                        }

                                        return (
                                            <Select
                                                labelId="services-label"
                                                id="services"
                                                name="services"
                                                label="Services"
                                                multiple
                                                value={validServices}
                                                sx={{
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "49px"
                                                        }
                                                    }
                                                }}
                                                onChange={(e) =>
                                                    onChangeFormHook(
                                                        e.target.value
                                                    )
                                                }
                                                renderValue={() =>
                                                    validServices.length >=
                                                    visibleServices.length
                                                        ? "All services"
                                                        : visibleServices
                                                              .filter(
                                                                  (service) =>
                                                                      validServices.includes(
                                                                          service.id
                                                                      )
                                                              )
                                                              .map(
                                                                  (service) =>
                                                                      service.name
                                                              )
                                                              .join(", ")
                                                }
                                            >
                                                {visibleServices.map(
                                                    (service) => (
                                                        <MenuItem
                                                            value={service.id}
                                                            key={service.id}
                                                        >
                                                            <Checkbox
                                                                checked={validServices.includes(
                                                                    service.id
                                                                )}
                                                            />
                                                            {service.name}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        );
                                    }}
                                />
                                {!!errors.services && (
                                    <FormHelperText error>
                                        {errors?.services.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        {travelType !== "COMPANY_LOCATIONS" && (
                            <>
                                <Grid item xs={12} sm={12}>
                                    <Controller
                                        name="address"
                                        control={control}
                                        rules={{
                                            required: "Please enter an address",
                                            validate: {
                                                validateAddress: () =>
                                                    travelType ===
                                                    "COMPANY_LOCATIONS"
                                                        ? true
                                                        : address
                                                          ? true
                                                          : false
                                            }
                                        }}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value: {
                                                    address,
                                                    addressAttributes
                                                }
                                            }
                                        }) => (
                                            <ClientAddressInput
                                                setAddress={(value) => {
                                                    onChangeFormHook(
                                                        value.addressOneLine
                                                    );
                                                    setValue(
                                                        "addressAttributes",
                                                        {
                                                            street: value.route,
                                                            city: value.city,
                                                            state: value.prov,
                                                            country:
                                                                value.country,
                                                            postalcode:
                                                                value.postalCode,
                                                            longitude:
                                                                value.coords
                                                                    .lng,
                                                            latitude:
                                                                value.coords.lat
                                                        }
                                                    );
                                                }}
                                                formWidth={"100%"}
                                                formMargin={"0px"}
                                                formMarginLeft={"0px"}
                                                label={"Anchor address"}
                                                addressOneLine={
                                                    getValues("address")
                                                        ? getValues("address")
                                                        : ""
                                                }
                                                autocompleteService={
                                                    autocompleteService
                                                }
                                                smallSize={true}
                                            />
                                        )}
                                    />
                                    {!!errors.address && (
                                        <FormHelperText error>
                                            {errors?.address.message}
                                        </FormHelperText>
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Controller
                                        name="polygons"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value: polygons
                                            }
                                        }) => (
                                            <MapComponent
                                                fromDashboard={() => {
                                                    return false;
                                                }}
                                                lat={
                                                    addressAttributes?.latitude
                                                }
                                                lng={
                                                    addressAttributes?.longitude
                                                }
                                                location={address}
                                                polygons={polygons}
                                                setPolygons={(polygon) => {
                                                    setValue(
                                                        "polygons",
                                                        [...polygons, polygon],
                                                        { shouldDirty: true }
                                                    );
                                                    clearErrors("polygons");
                                                }}
                                                undoPolygon={() =>
                                                    setValue(
                                                        "polygons",
                                                        polygons.slice(0, -1),
                                                        { shouldDirty: true }
                                                    )
                                                }
                                                radius={
                                                    travelDistance *
                                                    (travelDistanceUnit ===
                                                    "kilometer"
                                                        ? 1000
                                                        : 1.60934 * 1000)
                                                }
                                                drawType={
                                                    drawTypeMap[travelType]
                                                }
                                                viewType={"Add"}
                                                deletePolygon={() =>
                                                    setValue(
                                                        "polygons",
                                                        polygons.slice(0, -1),
                                                        { shouldDirty: true }
                                                    )
                                                }
                                                mapHeight={"300px"}
                                                mapWidth={"100%"}
                                                hideSaveClose={true}
                                            />
                                        )}
                                    />
                                    {!!errors.polygons &&
                                        travelType === "DRAW_TRAVEL_REGION" && (
                                            <FormHelperText error>
                                                {errors?.polygons.message}
                                            </FormHelperText>
                                        )}
                                </Grid>
                            </>
                        )}
                        {selectedSchedule && (
                            <>
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    sx={{
                                        paddingTop:
                                            travelType === "MAX_DISTANCE"
                                                ? "18px !important"
                                                : "27px !important"
                                    }}
                                >
                                    <Button
                                        onClick={handleDuplicate}
                                        variant="text"
                                        disableRipple
                                    >
                                        <Typography
                                            variant="button"
                                            sx={{
                                                textTransform: "none",
                                                lineHeight: "14px"
                                            }}
                                        >
                                            {`Duplicate availability`}
                                        </Typography>
                                    </Button>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    sx={{
                                        paddingTop: "27px !important"
                                    }}
                                >
                                    <Button
                                        onClick={() => {
                                            setConfirmationFunction(() =>
                                                handleDelete()
                                            );
                                            setConfirmationDialogContent(
                                                "Are you sure you want to delete this availability?"
                                            );
                                            setConformationDialogTitle(
                                                "Delete availability"
                                            );
                                            setConformationDialogSubmitButton(
                                                "Delete"
                                            );
                                            setConfirmationDialogOpen(true);
                                        }}
                                        variant="text"
                                        color="error"
                                        sx={{
                                            "&:hover": {
                                                backgroundColor:
                                                    "transparent !important"
                                            }
                                        }}
                                        disableRipple
                                    >
                                        <Typography
                                            variant="button"
                                            sx={{
                                                textTransform: "none",
                                                lineHeight: "14px"
                                            }}
                                        >
                                            {`Delete availability`}
                                        </Typography>
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </LocalizationProvider>
            </DialogContent>
            <DialogActions
                sx={{
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >
                <Grid
                    display={"flex"}
                    gap={"18px"}
                    width={!isDesktop ? "100%" : "auto"}
                >
                    {isDesktop && (
                        <Button onClick={handleClose} variant="cancelButton">
                            Cancel
                        </Button>
                    )}
                    <LoadingButton
                        color="primary"
                        variant="contained"
                        loading={isSubmitting}
                        disabled={selectedSchedule && !isDirty}
                        type="submit"
                        sx={{ width: !isDesktop ? "100%" : "auto" }}
                    >
                        Save
                    </LoadingButton>
                </Grid>
            </DialogActions>
        </form>
    );
};
